import { VdpElement, CSVData } from '../types';
import { toPng, toSvg } from 'html-to-image';
import { 
  getStarSvgPath, 
  getPolygonSvgPath, 
  getTriangleSvgPath, 
  getDiamondSvgPath, 
  getHeartSvgPath, 
  getArrowSvgPath, 
  getLineEffectSvgPath, 
  formatCurrencyValue, 
  formatDateValue 
} from './shapeAndFormatUtils';
import { pointsToSvgPath } from './vectorPathUtils';

export interface QuickExportOptions {
  targetElementId?: string; // If specified, export only this element. Otherwise export all selectedElements
  isTransparentBg?: boolean;
  scale?: number; // 1, 2, 4, 8
  csvData?: CSVData | null;
  recordIndex?: number;
}

/**
 * Calculates the bounding box for a set of elements
 */
export function getElementsBoundingBox(elements: VdpElement[]) {
  if (elements.length === 0) {
    return { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  elements.forEach(el => {
    const w = el.width || 100;
    const h = el.height || 100;
    minX = Math.min(minX, el.x);
    minY = Math.min(minY, el.y);
    maxX = Math.max(maxX, el.x + w);
    maxY = Math.max(maxY, el.y + h);
  });

  if (!isFinite(minX)) minX = 0;
  if (!isFinite(minY)) minY = 0;
  if (!isFinite(maxX)) maxX = 100;
  if (!isFinite(maxY)) maxY = 100;

  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);

  return { minX, minY, maxX, maxY, width, height };
}

/**
 * Creates a temporary DOM container for capturing element(s) cleanly without selection rings or handles.
 */
function createExportContainer(elementsToExport: VdpElement[], isTransparent: boolean): { container: HTMLDivElement; cleanup: () => void } {
  const { minX, minY, width, height } = getElementsBoundingBox(elementsToExport);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '-99999px';
  container.style.width = `${Math.ceil(width)}px`;
  container.style.height = `${Math.ceil(height)}px`;
  container.style.backgroundColor = isTransparent ? 'transparent' : '#ffffff';
  container.style.overflow = 'hidden';
  container.style.boxSizing = 'border-box';
  container.style.zIndex = '-99999';

  elementsToExport.forEach(el => {
    const origNode = document.getElementById(`el-${el.id}`);
    if (origNode) {
      const clone = origNode.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('.export-ignore').forEach(node => node.remove());
      clone.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-1', 'ring-offset-white/20', 'shadow-lg', 'z-30');
      // If it has ring-2 added by tailwind dynamically, it will be stripped.
      // Position relative to bounding box top-left
      clone.style.left = `${el.x - minX}px`;
      clone.style.top = `${el.y - minY}px`;
      clone.style.margin = '0';
      
      // Strip selection ring highlights & active borders
      clone.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-1', 'z-30');
      clone.style.boxShadow = 'none';

      // Remove resize / rotate handles
      const handles = clone.querySelectorAll('.cursor-se-resize, .cursor-grab');
      handles.forEach(h => h.remove());

      container.appendChild(clone);
    }
  });

  document.body.appendChild(container);

  const cleanup = () => {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  };

  return { container, cleanup };
}

/**
 * Exports selected element(s) as a transparent or solid PNG
 */
export async function exportElementsAsPng(
  elements: VdpElement[],
  options: QuickExportOptions = {}
): Promise<void> {
  const {
    targetElementId,
    isTransparentBg = true,
    scale = 4
  } = options;

  const targetElements = targetElementId
    ? elements.filter(e => e.id === targetElementId)
    : elements;

  if (targetElements.length === 0) return;

  const { container, cleanup } = createExportContainer(targetElements, isTransparentBg);

  try {
    const dataUrl = await toPng(container, {
      pixelRatio: scale,
      backgroundColor: isTransparentBg ? undefined : '#ffffff',
      cacheBust: true
    });

    // Create download link
    const filename = targetElements.length === 1
      ? `element-${targetElements[0].type}-${targetElements[0].id.slice(0, 4)}-${scale}x.png`
      : `export-selection-group-${targetElements.length}items-${scale}x.png`;

    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error('Quick Export PNG failed:', err);
  } finally {
    cleanup();
  }
}

/**
 * Helper to escape XML strings
 */
function escapeXml(unsafe: string): string {
  return String(unsafe || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Generates a pure standalone SVG vector string for selected element(s)
 */
export function generateVectorSvgString(
  elements: VdpElement[],
  options: QuickExportOptions = {}
): string {
  const {
    targetElementId,
    isTransparentBg = true,
    csvData,
    recordIndex = 0
  } = options;

  const targetElements = targetElementId
    ? elements.filter(e => e.id === targetElementId)
    : elements;

  if (targetElements.length === 0) return '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"/>';

  const { minX, minY, width, height } = getElementsBoundingBox(targetElements);

  let innerContent = '';

  targetElements.forEach(el => {
    const relX = el.x - minX;
    const relY = el.y - minY;
    const w = el.width || 100;
    const h = el.height || 100;
    const rot = el.rotation || 0;
    const opacity = el.opacity !== undefined ? el.opacity : 1;
    const color = el.color || '#000000';
    const borderColor = el.borderColor || el.strokeColor || '#000000';
    const borderWidth = el.borderWidth || el.strokeWidth || 0;
    const cornerRadius = el.cornerRadius ?? el.borderRadius ?? 0;

    // Get display value if text or barcode
    const fallbackText = el.content ?? el.text ?? el.staticValue ?? el.value ?? el.name ?? '';
    let displayVal = fallbackText;
    if (el.columnName && el.columnName.trim() !== '' && csvData && Array.isArray(csvData.rows) && csvData.rows[recordIndex]) {
      const colName = el.columnName.trim();
      const csvVal = csvData.rows[recordIndex][colName];
      if (csvVal !== undefined && csvVal !== null && String(csvVal).trim() !== '') {
        displayVal = String(csvVal);
      }
    }
    if (el.type === 'uppercase') {
      displayVal = String(displayVal).toUpperCase();
    } else if (el.type === 'currency') {
      displayVal = formatCurrencyValue(displayVal, el.currencySymbol || '₹');
    } else if (el.type === 'date') {
      displayVal = formatDateValue(displayVal, el.dateFormat || 'DD/MM/YYYY');
    } else if (el.type === 'serial') {
      const prefix = el.serialPrefix !== undefined ? el.serialPrefix : 'SN-';
      const startNum = (el.serialStart || 1) + recordIndex;
      const padding = el.serialPadding || 4;
      displayVal = `${prefix}${String(startNum).padStart(padding, '0')}`;
    }

    const transformAttr = rot ? `transform="rotate(${rot} ${relX + w / 2} ${relY + h / 2})"` : '';
    const opacityAttr = opacity < 1 ? `opacity="${opacity}"` : '';

    let elementSvg = '';

    if (['text', 'uppercase', 'currency', 'date', 'serial'].includes(el.type)) {
      const fontSize = el.fontSize || 14;
      const fontFamily = el.fontFamily || 'sans-serif';
      const fontWeight = el.isBold ? 'font-weight="bold"' : '';
      const fontStyle = el.isItalic ? 'font-style="italic"' : '';
      const anchor = el.textAlign === 'center' ? 'middle' : el.textAlign === 'right' ? 'end' : 'start';
      const textX = el.textAlign === 'center' ? relX + w / 2 : el.textAlign === 'right' ? relX + w : relX;
      const textY = relY + fontSize;

      elementSvg = `<text x="${textX}" y="${textY}" font-size="${fontSize}" font-family="${escapeXml(fontFamily)}" fill="${color}" text-anchor="${anchor}" ${fontWeight} ${fontStyle}>${escapeXml(displayVal || 'Text')}</text>`;
    } else if (el.type === 'rectangle') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#e2e8f0');
      elementSvg = `<rect x="${relX}" y="${relY}" width="${w}" height="${h}" rx="${cornerRadius}" ry="${cornerRadius}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/>`;
    } else if (el.type === 'circle') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#e2e8f0');
      elementSvg = `<ellipse cx="${relX + w / 2}" cy="${relY + h / 2}" rx="${w / 2}" ry="${h / 2}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/>`;
    } else if (el.type === 'star') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#f59e0b');
      const starPath = getStarSvgPath(w, h, el.starPoints || 5, el.starInnerRadiusRatio || 0.4, cornerRadius);
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${starPath}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/></g>`;
    } else if (el.type === 'polygon') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#3b82f6');
      const polyPath = getPolygonSvgPath(w, h, el.polygonSides || 6, cornerRadius);
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${polyPath}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/></g>`;
    } else if (el.type === 'triangle') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#3b82f6');
      const triPath = getTriangleSvgPath(w, h);
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${triPath}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/></g>`;
    } else if (el.type === 'diamond') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#3b82f6');
      const diaPath = getDiamondSvgPath(w, h);
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${diaPath}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/></g>`;
    } else if (el.type === 'heart') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#ec4899');
      const heartPath = getHeartSvgPath(w, h);
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${heartPath}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/></g>`;
    } else if (el.type === 'arrow') {
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : '#3b82f6');
      const arrowPath = getArrowSvgPath(w, h);
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${arrowPath}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/></g>`;
    } else if (el.type === 'line') {
      const linePath = getLineEffectSvgPath(w, h, el.lineEffect || 'none', el.effectAmplitude, el.effectFrequency, el.effectPointsStyle);
      const dash = el.lineDash === 'dashed' ? 'stroke-dasharray="6 4"' : el.lineDash === 'dotted' ? 'stroke-dasharray="2 2"' : '';
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${linePath}" fill="none" stroke="${borderColor}" stroke-width="${borderWidth || 2}" stroke-linecap="${el.lineCap || 'round'}" ${dash}/></g>`;
    } else if (el.type === 'path' || el.type === 'pen') {
      const pData = el.pathData || el.pathD || (el.points ? pointsToSvgPath(el.points, el.closedPath, el.isSmooth) : '');
      const fillColor = (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (color && color !== 'none' ? color : 'none');
      elementSvg = `<g transform="translate(${relX}, ${relY})"><path d="${pData}" fill="${fillColor}" stroke="${borderColor}" stroke-width="${borderWidth || 2}"/></g>`;
    } else if (el.type === 'image') {
      const imgSrc = el.src || el.staticValue || '';
      elementSvg = `<image x="${relX}" y="${relY}" width="${w}" height="${h}" href="${escapeXml(imgSrc)}" preserveAspectRatio="none"/>`;
    } else {
      // Fallback for barcodes / qr / datamatrix: captured visually or rendered box with text
      elementSvg = `<rect x="${relX}" y="${relY}" width="${w}" height="${h}" rx="${cornerRadius}" fill="${el.backgroundColor || '#ffffff'}" stroke="${borderColor}" stroke-width="${borderWidth}"/>
      <text x="${relX + w / 2}" y="${relY + h / 2}" font-size="12" font-family="monospace" fill="${color}" text-anchor="middle" dominant-baseline="middle">${escapeXml(displayVal || el.type.toUpperCase())}</text>`;
    }

    innerContent += `\n  <g ${transformAttr} ${opacityAttr}>${elementSvg}</g>`;
  });

  const bgRect = !isTransparentBg
    ? `\n  <rect width="100%" height="100%" fill="#ffffff"/>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${Math.ceil(width)} ${Math.ceil(height)}" width="${Math.ceil(width)}" height="${Math.ceil(height)}">${bgRect}${innerContent}\n</svg>`;
}

/**
 * Exports selected element(s) as high-resolution SVG file
 */
export async function exportElementsAsSvg(
  elements: VdpElement[],
  options: QuickExportOptions = {}
): Promise<void> {
  const { targetElementId, isTransparentBg = true } = options;

  const targetElements = targetElementId
    ? elements.filter(e => e.id === targetElementId)
    : elements;

  if (targetElements.length === 0) return;

  const { container, cleanup } = createExportContainer(targetElements, isTransparentBg);

  try {
    // Try html-to-image DOM to SVG for exact visual fidelity
    const dataUrl = await toSvg(container, {
      backgroundColor: isTransparentBg ? undefined : '#ffffff',
      cacheBust: true
    });

    const filename = targetElements.length === 1
      ? `element-${targetElements[0].type}-${targetElements[0].id.slice(0, 4)}.svg`
      : `export-selection-group-${targetElements.length}items.svg`;

    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.warn('DOM to SVG failed, using vector string fallback:', err);
    // Fallback to vector string builder
    const svgStr = generateVectorSvgString(elements, options);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const filename = targetElements.length === 1
      ? `element-${targetElements[0].type}-${targetElements[0].id.slice(0, 4)}.svg`
      : `export-selection-group-${targetElements.length}items.svg`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } finally {
    cleanup();
  }
}

/**
 * Copies the SVG string of element(s) directly to clipboard
 */
export async function copyElementsAsSvgCode(
  elements: VdpElement[],
  options: QuickExportOptions = {}
): Promise<boolean> {
  try {
    const svgStr = generateVectorSvgString(elements, options);
    await navigator.clipboard.writeText(svgStr);
    return true;
  } catch (err) {
    console.error('Failed to copy SVG:', err);
    return false;
  }
}
